import React, {useEffect, useState} from 'react';
import PatientDashboardLayout from './PatientDashboardLayout';
import {useBaseUrl} from '../utils/utils';
import localForage from 'localforage';

/**
 * Was a tab shell with "coming soon" behind all four tabs. This wires the
 * one tab that has a real, matching data source today
 * (facilities/patient-clinics/<id>, added alongside this - every existing
 * ClinicPatient lookup filtered by clinic, none by patient). The other
 * three tabs (Collaborative/Referral/History) don't have an equivalent
 * patient-scoped endpoint yet, so they say so honestly rather than
 * rendering an empty list that looks like "you have none".
 */

type ClinicEnrollment = {
    clinic_id: number;
    clinic_name: string;
    clinic_website: string;
    clinic_logo: string | null;
    date_enrolled: string;
};

const tabList = [
    {key: 'my', label: 'My Facilities'},
    {key: 'collaborative', label: 'Collaborative Facilities'},
    {key: 'referral', label: 'Referral Facilities'},
    {key: 'history', label: 'History'},
];

const PatientFacilities: React.FC = () => {
    const [currentTab, setCurrentTab] = useState('my');
    const [clinics, setClinics] = useState<ClinicEnrollment[]>([]);
    const [loading, setLoading] = useState(true);
    const baseUrl = useBaseUrl();

    useEffect(() => {
        document.title = "Facilities - Uzima Healthcare";
        (async () => {
            try {
                const patientId = await localForage.getItem<string>('patient_id');
                if (!patientId) return;
                const response = await fetch(`${baseUrl}facilities/patient-clinics/${patientId}`);
                const data = await response.json();
                if (data.status === 'success') {
                    setClinics(data.data || []);
                }
            } catch (error) {
                console.error('Error fetching clinics:', error);
            } finally {
                setLoading(false);
            }
        })();
    }, [baseUrl]);

    return (
        <PatientDashboardLayout>
            <div className="container-fluid">
                <div className="row mb-4">
                    <div className="col-12">
                        <h2 className="mb-1">Facilities</h2>
                    </div>
                </div>

                <ul className="nav nav-tabs mb-4">
                    {tabList.map((tab) => (
                        <li className="nav-item" key={tab.key}>
                            <button
                                className={`nav-link ${currentTab === tab.key ? 'active' : ''}`}
                                onClick={() => setCurrentTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        </li>
                    ))}
                </ul>

                {currentTab === 'my' && (
                    loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : clinics.length === 0 ? (
                        <p className="text-muted">You are not enrolled at any clinic yet.</p>
                    ) : (
                        <div className="row">
                            {clinics.map((c) => (
                                <div key={c.clinic_id} className="col-md-6 col-lg-4 mb-4">
                                    <div className="card h-100">
                                        <div className="card-body">
                                            <h5 className="card-title mb-1">{c.clinic_name}</h5>
                                            {c.clinic_website && <p className="text-muted small mb-2">{c.clinic_website}</p>}
                                            <p className="small text-muted mb-0">Enrolled {new Date(c.date_enrolled).toLocaleDateString()}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                )}

                {currentTab !== 'my' && (
                    <p className="text-muted">This view isn't available yet.</p>
                )}
            </div>
        </PatientDashboardLayout>
    );
};

export default PatientFacilities;
